<script setup lang="ts">
import type { CondosNeighborhoodsProps } from '.';
import type { Condo } from '~/core/entities/condo/Condo';
import type { CondoFilters } from '~/core/entities/condo/Filters';
import type { PropertyTypeEnum } from '~/core/entities/CRMEnums';

import { propertyTypeByCondoType } from '~/core/entities/SystemEnums';
import { CACHE_TIME, cn } from '~/utils';
import { nuxtCondosRepository } from '~~/adapters/repositories/nuxt';

const props = defineProps<CondosNeighborhoodsProps>();

const SECTION_NAME = 'pre footer';

const key = computed(() => {
	const normalizedRegion = normalizeString(props.region);
	const normalizedCity = normalizeString(props.city);
	const normalizedState = normalizeString(props.state);
	const normalizedPropertyType = normalizeString(props.propertyType);
	const neighborhoodKey = [
		normalizedRegion,
		normalizedCity,
		normalizedState,
		normalizedPropertyType
	]
		.filter(Boolean)
		.join('-');

	return `listing:condos-neighborhoods-${neighborhoodKey}`;
});

const query = computed<CondoFilters>(() => ({
	region: props.region,
	...(props.city ? { city: props.city } : {}),
	...(props.state ? { state: props.state } : {}),
	...(props.propertyType
		? {
				condoType:
					propertyTypeByCondoType[props.propertyType as PropertyTypeEnum]
			}
		: {})
}));

const defaultCondos = { payload: [] as Condo[] };

const {
	data: condos,
	status: status,
	execute
} = useAsyncData(
	key.value,
	async () => {
		try {
			const filters = {
				page: 1,
				perPage: 12,
				...query.value
			};

			const [result, error] = await nuxtCondosRepository.list(filters);

			if (error || !result?.condos?.length) {
				return defaultCondos;
			}

			return {
				payload: result.condos
			};
		} catch {
			return defaultCondos;
		}
	},
	{
		default: () => defaultCondos,
		lazy: true,
		...getCacheAsyncDataConfig<{ payload: Condo[] }>(CACHE_TIME)
	}
);

const isLoadingProperties = computed(() => {
	return status.value === 'pending';
});

const title = computed(() => {
	return `Condomínios em ${props.region}`;
});

const isEmpty = computed(() => {
	return condos.value.payload.length === 0;
});

watch(
	query,
	newQuery => {
		if (newQuery.region) {
			execute();
		}
	},
	{ deep: true }
);
</script>

<template>
	<StandardSlider
		v-if="!isEmpty"
		:items="condos.payload"
		:title="title"
		:is-loading="isLoadingProperties"
		:section-name="SECTION_NAME"
		carousel-item-size="half-responsive"
		title-as="h2"
		title-classes="md:text-[32px]"
		:container-class="cn(containerClass, '!px-0')"
	>
		<template #items="{ item, index }">
			<NuxtLink
				:to="`/condominio/${item.slug}`"
				target="_blank"
				rel="noopener noreferrer"
				class="flex min-h-full"
			>
				<CondoCard
					:condo="item"
					:section="SECTION_NAME"
					variant="whiteBackground"
					:card-position="index"
					title-as="h3"
				/>
			</NuxtLink>
		</template>
		<template #cta></template>
	</StandardSlider>
</template>
